import React, { useCallback, useEffect, useMemo, useState } from 'react';
import WalterLoader from '../shared/WalterLoader';
import { FaExclamationCircle, FaKey, FaLink, FaPen, FaSignOutAlt, FaTrash } from 'react-icons/fa';
import { ROLE_LABELS, useAuth } from '../../auth/AuthContext';
import { adminApi, MCP_URL } from '../../auth/authApi';
import '../../styles/Admin.css';
import '../../styles/Auth.css';

const API_URL = process.env.REACT_APP_API_URL;
const CLIENT_ROLES = ['client_editeur', 'client_lecteur'];
const ROLE_ORDER = ['admin', 'auditeur', 'client_editeur', 'client_lecteur'];

const ROLE_HINTS = {
    admin: "Tout, plus la gestion des comptes et des jetons.",
    auditeur: "Tous les clients et toutes les écritures, référentiels et export du rapport compris.",
    client_editeur: "Crée, modifie et supprime les bâtiments de ses clients (accès ouvert à l'étape 6 du plan).",
    client_lecteur: "Consulte les bâtiments de ses clients (accès ouvert à l'étape 6 du plan).",
};

function formatDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function userStatus(user) {
    if (!user.active) return { label: 'Désactivé', tone: 'muted' };
    if (!user.has_password) return { label: 'Invitation en attente', tone: 'pending' };
    if (user.locked) return { label: 'Verrouillé (15 min)', tone: 'danger' };
    return { label: 'Actif', tone: 'ok' };
}

/* ─────────────────────────────────────────────
   Page /admin/users (AUTH_PLAN.md §5.3) : comptes, invitations, liens de
   réinitialisation, sessions et jetons API du connecteur MCP. Réservée à l'admin
   (l'API refuse les autres rôles quel que soit AUTH_ENABLED). Les liens partent
   par email (api/mailer.py) et restent affichés une fois, copiables en secours ;
   les jetons ne sont affichés qu'à leur création. Rien n'est récupérable
   ensuite : il faut en générer un nouveau.
───────────────────────────────────────────── */
const UsersAdmin = () => {
    const { user: currentUser, isAdmin, status } = useAuth();
    const [users, setUsers] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modal, setModal] = useState(null);

    const clientNames = useMemo(
        () => Object.fromEntries(clients.map((client) => [client._id, client.name_client])),
        [clients]
    );

    const load = useCallback(async () => {
        try {
            const [usersData, clientsData] = await Promise.all([
                adminApi.listUsers(),
                fetch(`${API_URL}/get_clients`).then((r) => (r.ok ? r.json() : [])),
            ]);
            setUsers(usersData.users);
            setClients([...clientsData].sort((a, b) => (a.name_client || '').localeCompare(b.name_client || '')));
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAdmin) load();
        else setLoading(false);
    }, [isAdmin, load]);

    const runAction = async (action) => {
        setError(null);
        try {
            await action();
            await load();
        } catch (err) {
            setError(err.message);
        }
    };

    const sendLink = (user) => {
        const what = user.has_password ? 'un lien de réinitialisation du mot de passe' : 'une nouvelle invitation';
        if (!window.confirm(`Envoyer ${what} à ${user.name} (${user.email}) par email ? Le lien précédent ne fonctionnera plus.`)) return;
        runAction(async () => {
            const link = await adminApi.createLink(user._id);
            setModal({ type: 'link', user, link });
        });
    };

    const deleteUser = (user) => {
        if (!window.confirm(
            `Supprimer définitivement le compte de ${user.name} (${user.email}) ? Ses sessions et ses adresses de connecteur Claude `
            + "seront supprimées. Pour une personne qui part, la désactivation (bouton crayon) suffit et peut être annulée."
        )) return;
        runAction(() => adminApi.deleteUser(user._id));
    };

    const revokeSessions = (user) => {
        if (!window.confirm(`Fermer toutes les sessions de ${user.name} ? Il devra se reconnecter sur chaque appareil.`)) return;
        runAction(() => adminApi.revokeSessions(user._id));
    };

    if (status === 'loading' || loading) {
        return (
            <div className="centered-spinner-dashboard">
                <WalterLoader />
            </div>
        );
    }

    if (!isAdmin) {
        return (
            <div className="admin-page-content admin-page-content--narrow-top">
                <div className="admin-empty-state">
                    {currentUser
                        ? "Cette page est réservée aux administrateurs."
                        : "Connectez-vous avec un compte administrateur pour gérer les comptes (menu « Se connecter » en haut à droite)."}
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page-content admin-page-content--narrow-top">
            {error && <div className="admin-error-banner"><FaExclamationCircle />{error}</div>}

            <div className="admin-card">
                <div className="admin-card-header">
                    <span className="admin-card-title">
                        <span className="admin-card-title-bar" />
                        Comptes
                    </span>
                    <div className="users-header-actions">
                        <span className="admin-count-badge">{users.length}</span>
                        <button type="button" className="admin-add-btn users-new-btn" onClick={() => setModal({ type: 'edit', user: null })}>
                            + Nouveau compte
                        </button>
                    </div>
                </div>

                {users.length === 0 && <div className="admin-empty-state">Aucun compte pour l'instant.</div>}

                {users.map((user) => {
                    const state = userStatus(user);
                    return (
                        <div key={user._id} className={`users-row${user.active ? '' : ' users-row--inactive'}`}>
                            <div className="users-identity">
                                <span className="users-name">
                                    {user.name}
                                    {user._id === currentUser._id && <span className="users-self"> (vous)</span>}
                                </span>
                                <span className="users-email">{user.email}</span>
                                {CLIENT_ROLES.includes(user.role) && (
                                    <span className="users-clients">
                                        {user.client_ids.map((id) => clientNames[id] || id).join(', ')}
                                    </span>
                                )}
                            </div>
                            <div className="users-meta">
                                <span className={`users-role users-role--${user.role}`}>{ROLE_LABELS[user.role] || user.role}</span>
                                <span className={`users-status users-status--${state.tone}`}>{state.label}</span>
                                <span className="users-detail">
                                    Dernière connexion : {formatDate(user.last_login_at)}
                                    {user.active_sessions > 0 && ` · ${user.active_sessions} session${user.active_sessions > 1 ? 's' : ''}`}
                                </span>
                            </div>
                            <div className="users-actions">
                                <button type="button" className="admin-icon-btn" title="Modifier"
                                    onClick={() => setModal({ type: 'edit', user })}>
                                    <FaPen />
                                </button>
                                <button type="button" className="admin-icon-btn" disabled={!user.active}
                                    title={user.has_password ? 'Envoyer un lien de réinitialisation par email' : "Renvoyer l'invitation par email"}
                                    onClick={() => sendLink(user)}>
                                    <FaLink />
                                </button>
                                <button type="button" className="admin-icon-btn" title="Jetons API (connecteur Claude)"
                                    onClick={() => setModal({ type: 'tokens', user })}>
                                    <FaKey />
                                </button>
                                <button type="button" className="admin-icon-btn admin-icon-btn--danger"
                                    disabled={user.active_sessions === 0} title="Fermer toutes les sessions"
                                    onClick={() => revokeSessions(user)}>
                                    <FaSignOutAlt />
                                </button>
                                <button type="button" className="admin-icon-btn admin-icon-btn--danger"
                                    disabled={user._id === currentUser._id}
                                    title={user._id === currentUser._id ? 'Impossible de supprimer votre propre compte' : 'Supprimer le compte'}
                                    onClick={() => deleteUser(user)}>
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {modal?.type === 'edit' && (
                <UserFormModal
                    user={modal.user}
                    clients={clients}
                    isSelf={modal.user?._id === currentUser._id}
                    onClose={() => setModal(null)}
                    onSaved={async (result) => {
                        await load();
                        setModal(result.link ? { type: 'link', user: result.user, link: result.link } : null);
                    }}
                />
            )}
            {modal?.type === 'link' && <LinkModal user={modal.user} link={modal.link} onClose={() => setModal(null)} />}
            {modal?.type === 'tokens' && <TokensModal user={modal.user} onClose={() => setModal(null)} />}
        </div>
    );
};

/* Création (avec lien d'invitation) ou modification d'un compte. */
const UserFormModal = ({ user, clients, isSelf, onClose, onSaved }) => {
    const [form, setForm] = useState({
        name: user?.name || '',
        email: user?.email || '',
        role: user?.role || 'auditeur',
        client_ids: user?.client_ids || [],
        active: user ? user.active : true,
    });
    const [sendEmail, setSendEmail] = useState(true);
    const [clientFilter, setClientFilter] = useState('');
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const isClientRole = CLIENT_ROLES.includes(form.role);

    const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
    const toggleClient = (id) => update(
        'client_ids',
        form.client_ids.includes(id) ? form.client_ids.filter((c) => c !== id) : [...form.client_ids, id]
    );

    const visibleClients = clients.filter((client) =>
        (client.name_client || '').toLowerCase().includes(clientFilter.trim().toLowerCase()));

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        setSaving(true);
        const payload = { ...form, client_ids: isClientRole ? form.client_ids : [] };
        try {
            const result = user
                ? await adminApi.updateUser(user._id, payload)
                : await adminApi.createUser({ ...payload, send_email: sendEmail });
            await onSaved(result);
        } catch (err) {
            setError(err.message);
            setSaving(false);
        }
    };

    return (
        <div className="auth-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="user-form-title">
            <div className="auth-modal auth-modal--wide">
                <h2 id="user-form-title" className="auth-title">{user ? `Modifier ${user.name}` : 'Nouveau compte'}</h2>
                {!user && (
                    <p className="auth-subtitle">
                        La personne reçoit par email un lien d'invitation (valable 7 jours) pour choisir son mot de passe.
                    </p>
                )}
                <form className="auth-form" onSubmit={handleSubmit}>
                    <div className="users-form-grid">
                        <div className="auth-field">
                            <label htmlFor="user-name">Nom</label>
                            <input id="user-name" type="text" required value={form.name}
                                onChange={(e) => update('name', e.target.value)} autoFocus />
                        </div>
                        <div className="auth-field">
                            <label htmlFor="user-email">Email</label>
                            <input id="user-email" type="email" required value={form.email}
                                onChange={(e) => update('email', e.target.value)} />
                        </div>
                    </div>
                    <div className="auth-field">
                        <label htmlFor="user-role">Rôle</label>
                        <select id="user-role" value={form.role} disabled={isSelf}
                            onChange={(e) => update('role', e.target.value)}>
                            {ROLE_ORDER.map((role) => <option key={role} value={role}>{ROLE_LABELS[role]}</option>)}
                        </select>
                        <span className="auth-hint">{ROLE_HINTS[form.role]}</span>
                    </div>
                    {isClientRole && (
                        <div className="auth-field">
                            <label>Clients rattachés ({form.client_ids.length})</label>
                            <input type="text" placeholder="Filtrer les clients" value={clientFilter}
                                onChange={(e) => setClientFilter(e.target.value)} />
                            <div className="users-client-list">
                                {visibleClients.map((client) => (
                                    <label key={client._id} className="users-client-option">
                                        <input type="checkbox" checked={form.client_ids.includes(client._id)}
                                            onChange={() => toggleClient(client._id)} />
                                        <span>{client.name_client}</span>
                                    </label>
                                ))}
                                {visibleClients.length === 0 && <span className="auth-hint">Aucun client ne correspond.</span>}
                            </div>
                        </div>
                    )}
                    {!user && (
                        <label className="users-client-option users-active-toggle">
                            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
                            <span>Envoyer l'invitation par email (sinon, le lien sera seulement affiché, à transmettre soi-même)</span>
                        </label>
                    )}
                    {user && !isSelf && (
                        <label className="users-client-option users-active-toggle">
                            <input type="checkbox" checked={form.active} onChange={(e) => update('active', e.target.checked)} />
                            <span>Compte actif (désactiver ferme ses sessions et coupe ses jetons API)</span>
                        </label>
                    )}
                    {error && <div className="auth-error" role="alert">{error}</div>}
                    <div className="auth-actions">
                        <button type="button" className="auth-btn auth-btn--ghost" onClick={onClose}>Annuler</button>
                        {user?.locked && (
                            <button type="button" className="auth-btn auth-btn--secondary" disabled={saving}
                                onClick={() => adminApi.updateUser(user._id, { unlock: true }).then(onSaved).catch((err) => setError(err.message))}>
                                Déverrouiller
                            </button>
                        )}
                        <button type="submit" className="auth-btn auth-btn--primary" disabled={saving}>
                            {saving ? 'Enregistrement…' : user ? 'Enregistrer' : sendEmail ? "Créer et envoyer l'invitation" : 'Créer et générer le lien'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const CopyField = ({ value }) => {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            window.prompt('Copier :', value);
        }
    };
    return (
        <div className="users-copy">
            <input type="text" readOnly value={value} onFocus={(e) => e.target.select()} />
            <button type="button" className="auth-btn auth-btn--secondary" onClick={copy}>{copied ? 'Copié' : 'Copier'}</button>
        </div>
    );
};

/* Résultat de l'envoi d'un lien d'invitation ou de réinitialisation. Le lien est affiché une
   seule fois, pour le transmettre soi-même si l'email n'est pas parti. */
const LinkModal = ({ user, link, onClose }) => {
    const isInvite = link.purpose === 'invite';
    const url = link.url || `${window.location.origin}/${isInvite ? 'invitation' : 'reset'}/${link.token}`;
    return (
        <div className="auth-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="link-title">
            <div className="auth-modal auth-modal--wide">
                <h2 id="link-title" className="auth-title">{isInvite ? "Lien d'invitation" : 'Lien de réinitialisation'}</h2>
                {link.email_sent ? (
                    <div className="users-mail-status users-mail-status--ok">Email envoyé à {user.email}.</div>
                ) : (
                    <div className="users-mail-status users-mail-status--warn">
                        {link.email_error
                            ? `L'email n'a pas pu partir (${link.email_error}). Transmettez le lien vous-même.`
                            : 'Aucun email envoyé : transmettez le lien vous-même.'}
                    </div>
                )}
                <p className="auth-subtitle">
                    {link.email_sent ? 'En secours, le même lien' : `Lien pour ${user.name}, à transmettre par un canal privé`} :
                    valable {isInvite ? '7 jours' : '1 heure'}, utilisable une seule fois ; un nouveau lien annule celui-ci.
                </p>
                <CopyField value={url} />
                <div className="auth-actions">
                    <button type="button" className="auth-btn auth-btn--primary" onClick={onClose}>Fermer</button>
                </div>
            </div>
        </div>
    );
};

/* Jetons API d'un compte : chacun donne accès au connecteur Claude (serveur MCP) avec les
   droits de ce compte. Le jeton en clair n'est affiché qu'à sa création. */
const TokensModal = ({ user, onClose }) => {
    const [tokens, setTokens] = useState(null);
    const [label, setLabel] = useState('Connecteur Claude');
    const [created, setCreated] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        adminApi.listTokens(user._id).then((data) => setTokens(data.tokens)).catch((err) => setError(err.message));
    }, [user._id]);

    useEffect(() => { load(); }, [load]);

    const create = async () => {
        setError(null);
        try {
            const result = await adminApi.createToken(user._id, label);
            setCreated(result.token);
            load();
        } catch (err) {
            setError(err.message);
        }
    };

    const revoke = async (token) => {
        if (!window.confirm(`Révoquer le jeton « ${token.label} » ? Le connecteur qui l'utilise cessera de fonctionner.`)) return;
        setError(null);
        try {
            await adminApi.revokeToken(token._id);
            load();
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <div className="auth-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="tokens-title">
            <div className="auth-modal auth-modal--wide">
                <h2 id="tokens-title" className="auth-title">Jetons API de {user.name}</h2>
                <p className="auth-subtitle">
                    Un jeton donne accès au connecteur Claude « Walter » avec les droits de ce compte. URL à ajouter dans
                    claude.ai → Customize → Connectors : <code>{MCP_URL}/&lt;jeton&gt;</code>.
                </p>
                {created && (
                    <div className="users-token-created">
                        <strong>Nouveau jeton — copiez l'URL maintenant, elle ne sera plus affichée :</strong>
                        <CopyField value={`${MCP_URL}/${created}`} />
                    </div>
                )}
                {error && <div className="auth-error" role="alert">{error}</div>}
                {tokens === null ? (
                    <span className="auth-hint">Chargement…</span>
                ) : tokens.length === 0 ? (
                    <span className="auth-hint">Aucun jeton.</span>
                ) : (
                    <div className="users-token-list">
                        {tokens.map((token) => (
                            <div key={token._id} className="users-token-row">
                                <span className="users-name">{token.label}</span>
                                <code>{token.prefix}…</code>
                                <span className="users-detail">
                                    Créé le {formatDate(token.created_at)} · utilisé {token.last_used_at ? `le ${formatDate(token.last_used_at)}` : 'jamais'}
                                </span>
                                <button type="button" className="auth-btn auth-btn--ghost users-token-revoke" onClick={() => revoke(token)}>
                                    Révoquer
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                <div className="users-copy">
                    <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Libellé du jeton" />
                    <button type="button" className="auth-btn auth-btn--secondary" onClick={create} disabled={!user.active}>
                        Créer un jeton
                    </button>
                </div>
                <div className="auth-actions">
                    <button type="button" className="auth-btn auth-btn--primary" onClick={onClose}>Fermer</button>
                </div>
            </div>
        </div>
    );
};

export default UsersAdmin;
